import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { playerEvents } from "../lib/api";
import { getGameName, getGameColor } from "../constants/theme";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Users, Calendar, MapPin, Check, X, Heart, Send, Trophy, Play, Loader2, Crown } from "lucide-react";

export default function PlayerGamePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [matches, setMatches] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [newMatch, setNewMatch] = useState([]);
  const [ranking, setRanking] = useState([]);
  const lastTs = useRef(null);
  const chatEnd = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await playerEvents.get(id);
      setEv(r.data);
      if (r.data.is_owner || r.data.status !== "open") {
        playerEvents.getMatches(id).then((m) => setMatches(m.data)).catch(() => {});
      }
      if (r.data.status === "finished") {
        playerEvents.ranking(id).then((rk) => setRanking(rk.data)).catch(() => {});
      }
    } catch { toast.error("Partie introuvable"); }
    finally { setLoading(false); }
  }, [id]);

  const loadMessages = useCallback(async () => {
    try {
      const r = await playerEvents.getMessages(id, lastTs.current);
      if (r.data.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...r.data.filter((m) => !seen.has(m.id))];
          lastTs.current = merged[merged.length - 1]?.created_at;
          return merged;
        });
      }
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => { load(); loadMessages(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { const t = setInterval(loadMessages, 5000); return () => clearInterval(t); }, [loadMessages]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!text.trim()) return;
    const t = text.trim(); setText("");
    try { await playerEvents.postMessage(id, t); await loadMessages(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleMatchPlayer = (pid) => setNewMatch((p) => p.includes(pid) ? p.filter((x) => x !== pid) : (p.length < 6 ? [...p, pid] : p));

  const createMatch = async () => {
    if (newMatch.length < 2) return toast.error("Sélectionnez 2 à 6 joueurs");
    try { await playerEvents.createMatch(id, { players: newMatch, winners: [] }); setNewMatch([]); const m = await playerEvents.getMatches(id); setMatches(m.data); toast.success("Partie créée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleWinner = async (match, pid) => {
    const winners = match.winners?.includes(pid) ? match.winners.filter((w) => w !== pid) : [...(match.winners || []), pid];
    try { await playerEvents.updateMatch(id, match.id, { winners }); const m = await playerEvents.getMatches(id); setMatches(m.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#7B68EE] animate-spin" /></div>;
  if (!ev) return <div className="text-center py-20 text-[#888]">Partie introuvable</div>;

  const name = (u) => u.pseudo || u.name || "Joueur";

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-10" data-testid="player-game-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#888] hover:text-white"><ArrowLeft className="w-4 h-4" />Retour</button>

      {/* Header */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-white text-xl font-bold" data-testid="pe-detail-title">{ev.title}</h1>
              <Badge style={{ backgroundColor: `${getGameColor(ev.game)}20`, color: getGameColor(ev.game) }}>{getGameName(ev.game)}</Badge>
              {ev.status === "started" && <Badge className="bg-green-500/15 text-green-400">En cours</Badge>}
            </div>
            <div className="flex items-center gap-3 text-[#888] text-sm mt-2 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(ev.date).toLocaleDateString("fr-FR")} {ev.start_time}</span>
              {ev.location?.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.location.city}</span>}
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{ev.confirmed_count}/{ev.max_players}</span>
            </div>
            {ev.description && <p className="text-[#B0B0B0] text-sm mt-3 whitespace-pre-line">{ev.description}</p>}
          </div>
          {ev.is_owner && ev.status === "open" && (
            <Button onClick={() => act(() => playerEvents.start(id), "Partie démarrée")} disabled={busy} className="bg-[#7B68EE] hover:bg-[#6A5ACD] gap-1.5" data-testid="pe-start-btn"><Play className="w-4 h-4" />Démarrer</Button>
          )}
          {ev.is_owner && ev.status === "started" && (
            <Button onClick={() => act(() => playerEvents.finish(id), "Soirée terminée")} disabled={busy} className="bg-[#FFD700]/20 text-[#FFD700] hover:bg-[#FFD700]/30 border border-[#FFD700]/40 gap-1.5" data-testid="pe-finish-btn"><Trophy className="w-4 h-4" />Terminer la soirée</Button>
          )}
        </div>
      </div>

      {/* Ranking (finished) */}
      {ev.status === "finished" && ranking.length > 0 && (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="pe-ranking-section">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD700]" />Classement de la soirée</h2>
          <div className="space-y-2">
            {ranking.map((r) => {
              const podium = r.rank === 1 ? "#FFD700" : r.rank === 2 ? "#C0C0C0" : r.rank === 3 ? "#CD7F32" : null;
              return (
                <div key={r.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#252525]" data-testid={`pe-rank-${r.id}`}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-black shrink-0" style={{ backgroundColor: podium ? `${podium}22` : "#33333a", color: podium || "#B0B0B0" }}>{r.rank}</span>
                  <span className="text-white text-sm font-medium flex-1 truncate">{r.name}</span>
                  <span className="text-xs text-[#888]">{r.won}V · {r.lost}D</span>
                  <span className="text-xs font-semibold text-[#7B68EE]">{r.win_rate}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirmed players (everyone sees) */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5">
        <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#4ECDC4]" />Joueurs validés ({ev.confirmed?.length || 0})</h2>
        <div className="flex flex-wrap gap-2" data-testid="pe-confirmed-list">
          {ev.confirmed?.length ? ev.confirmed.map((u) => (
            <span key={u.id} className="px-3 py-1.5 rounded-full bg-[#252525] text-white text-sm">{name(u)}</span>
          )) : <p className="text-[#666] text-sm italic">Aucun joueur validé pour l'instant</p>}
        </div>
      </div>

      {/* Owner: pending / refused management */}
      {ev.is_owner && (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="pe-pending-section">
          <h2 className="text-white font-semibold mb-3">En attente ({ev.pending?.length || 0})</h2>
          <div className="space-y-2">
            {ev.pending?.length ? ev.pending.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#252525]">
                <span className="text-white text-sm">{name(u)}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={() => act(() => playerEvents.confirm(id, u.id), "Confirmé")} className="bg-green-600 hover:bg-green-700 h-8 gap-1" data-testid={`pe-confirm-${u.id}`}><Check className="w-3.5 h-3.5" />Confirmer</Button>
                  <Button size="sm" variant="outline" onClick={() => act(() => playerEvents.refuse(id, u.id), "Refusé")} className="border-[#333] text-[#888] hover:text-red-400 h-8 gap-1" data-testid={`pe-refuse-${u.id}`}><X className="w-3.5 h-3.5" />Refuser</Button>
                </div>
              </div>
            )) : <p className="text-[#666] text-sm italic">Personne en attente</p>}
          </div>
        </div>
      )}

      {/* Interest button (non-owner) */}
      {!ev.is_owner && isAuthenticated && (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5 text-center">
          {ev.my_state === "confirmed" ? (
            <p className="text-green-400 font-medium flex items-center justify-center gap-2"><Check className="w-5 h-5" />Vous êtes validé pour cette partie !</p>
          ) : ev.my_state === "refused" ? (
            <p className="text-red-400 text-sm">Votre demande n'a pas été retenue.</p>
          ) : ev.my_state === "pending" ? (
            <div className="space-y-2">
              <p className="text-amber-400 text-sm">En attente de validation par l'organisateur…</p>
              <Button variant="outline" onClick={() => act(() => playerEvents.removeInterest(id))} className="border-[#333] text-[#888]" data-testid="pe-remove-interest">Retirer mon intérêt</Button>
            </div>
          ) : (
            <Button onClick={() => act(() => playerEvents.interest(id), "Intérêt manifesté !")} disabled={busy || ev.is_full} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-2 h-11 px-6" data-testid="pe-interest-btn">
              <Heart className="w-4 h-4" />{ev.is_full ? "Partie complète" : "Je suis intéressé"}
            </Button>
          )}
        </div>
      )}

      {/* Matches (started) */}
      {(ev.status === "started" || matches.length > 0) && (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="pe-matches-section">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD700]" />Parties</h2>
          {ev.is_owner && ev.status === "started" && (
            <div className="mb-4 p-3 rounded-lg bg-[#151515] border border-[#333]">
              <p className="text-xs text-[#888] mb-2">Sélectionnez 2 à 6 joueurs pour une nouvelle partie :</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[...(ev.confirmed || []), { id: ev.owner_id, pseudo: ev.owner_name, name: ev.owner_name }]
                  .filter((u, i, arr) => arr.findIndex((x) => x.id === u.id) === i)
                  .map((u) => (
                  <button key={u.id} onClick={() => toggleMatchPlayer(u.id)} className={`px-2.5 py-1 rounded-full text-xs ${newMatch.includes(u.id) ? "bg-[#7B68EE] text-white" : "bg-[#252525] text-[#B0B0B0]"}`} data-testid={`pe-pick-${u.id}`}>{name(u)}{u.id === ev.owner_id ? " (moi)" : ""}</button>
                ))}
              </div>
              <Button size="sm" onClick={createMatch} className="bg-[#7B68EE] hover:bg-[#6A5ACD]" data-testid="pe-create-match">Créer la partie</Button>
            </div>
          )}
          <div className="space-y-2">
            {matches.map((m, i) => (
              <div key={m.id} className="p-3 rounded-lg bg-[#252525]" data-testid={`pe-match-${m.id}`}>
                <p className="text-[#888] text-xs mb-1.5">Partie #{i + 1}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.players_info?.map((u) => {
                    const won = m.winners?.includes(u.id);
                    return ev.is_owner ? (
                      <button key={u.id} onClick={() => toggleWinner(m, u.id)} className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${won ? "bg-[#FFD700]/20 text-[#FFD700]" : "bg-[#1a1a1a] text-[#B0B0B0]"}`} data-testid={`pe-winner-${m.id}-${u.id}`}>
                        {won && <Crown className="w-3 h-3" />}{name(u)}
                      </button>
                    ) : (
                      <span key={u.id} className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${won ? "bg-[#FFD700]/20 text-[#FFD700]" : "bg-[#1a1a1a] text-[#B0B0B0]"}`}>{won && <Crown className="w-3 h-3" />}{name(u)}</span>
                    );
                  })}
                </div>
                {ev.is_owner && <p className="text-[10px] text-[#666] mt-1.5">Cliquez sur un joueur pour le marquer vainqueur (plusieurs possibles)</p>}
              </div>
            ))}
            {matches.length === 0 && <p className="text-[#666] text-sm italic">Aucune partie enregistrée</p>}
          </div>
        </div>
      )}

      {/* Public chat */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5" data-testid="pe-chat-section">
        <h2 className="text-white font-semibold mb-3">Questions & discussion</h2>
        <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
          {messages.map((m) => (
            <div key={m.id} className="flex gap-2" data-testid="pe-chat-msg">
              <div className="min-w-0">
                <span className="text-[#7B68EE] text-xs font-semibold">{m.user_name}</span>
                <span className="text-[#555] text-[10px] ml-2">{new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                <p className="text-[#E0E0E0] text-sm break-words">{m.text}</p>
              </div>
            </div>
          ))}
          {messages.length === 0 && <p className="text-[#666] text-sm italic">Aucun message. Posez votre question !</p>}
          <div ref={chatEnd} />
        </div>
        {isAuthenticated && (
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" className="bg-[#252525] border-[#333] text-white" data-testid="pe-chat-input" />
            <Button onClick={send} className="bg-[#7B68EE] hover:bg-[#6A5ACD] shrink-0" data-testid="pe-chat-send"><Send className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );
}
